/**
 * Canonical set of entity types that should never render as a row in a
 * *generic* tree/list view (Drive's entity browser, sidebar's displayable
 * entities, etc.) — regardless of whether the entity has a dataspace edge.
 *
 * This answers a different question than the backend's
 * `services.edge_service.is_tree_visible_entity_type()`: that predicate
 * decides whether a type ever gets a `dataspace_edges` row at all. This
 * module decides, given a node that already has an edge, whether the
 * *generic* renderer should show it — some edge-having types (Integration.*,
 * LocalMount, Automation, …) are intentionally hidden from generic rendering
 * because a bespoke UI (mail integration view, mount browser, automations
 * list, …) renders them instead.
 *
 * Previously this set was independently hand-maintained in three places
 * (gui/src/utils/entityVisibility.ts, gui/src/apps/RiverDriveApp/utils/hiddenEntityTypes.ts,
 * and an inline array in SpaceSidebarV2.tsx) and had drifted out of sync.
 * This is the single shared core; callers may still layer a small number of
 * additional, deliberately surface-specific exclusions on top (e.g. Drive
 * additionally hides Automation/Project/LocalMount because those have their
 * own dedicated apps/browsers, while the Space sidebar tree intentionally
 * keeps them visible as folder-like nodes) — those stay local to the
 * calling file, clearly commented, rather than living here.
 */

export const GENERIC_TREE_HIDDEN_ENTITY_TYPES: ReadonlySet<string> = new Set([
  // Thread/message content — rendered inline by dedicated chat/comment UIs,
  // never as standalone tree rows.
  'Thread',
  'Message',
  'CommentThread',
  'Mail.Message',
  'Mail.Thread',
  'Mail.Folder',
  'Mail.Draft',
  'Messaging.Message',
  'Crisp.Message',

  // Internal/system bookkeeping — never user-organizable documents.
  'DocVersion',
  'Window',
  'SearchResults',
  'ManuscriptDiscoveryJob',
  'RiverAppStorage',
  'TableRow',
  'Icd10xQuery',
  'WizardRun',
  'OutboundCampaign',
  'InviteLink',

  // Desktops — transparent containers rendered as their own concept, not a
  // generic list row.
  'Space',

  // The org container record itself (e.g. "<org name>'s Team") — never a
  // user-organizable document. Has no dataspace edge so it never reaches the
  // tree-based sidebar/Drive views, but generic-list surfaces that bypass the
  // tree (Drive's org-wide Recents, EntityPicker's Recents mode) fetch
  // directly from the entities collection and must filter it out explicitly.
  'Org',

  // Integration-sourced data records — queryable via river.entities.find() in
  // apps, but not user-organizable documents; shown via their own dedicated
  // app UI (BankApp, ContactsApp, etc.) instead of a generic tree row.
  'APIConnection',
  'CLIConnection',
  'Integration',
  'BankTransaction',
  'PlaidItem',
  'PlaidTransaction',
  'PlaidAccount',
  'GoogleDriveFile',
  // CRM Contact entities belong in the generic tree/sidebar; only
  // Mail.Contact (integration-sourced address-book rows) stay hidden.
  'Mail.Contact',
  'Social.Tweet',

  // Managed by dedicated apps (TasksApp, AutomationsApp, desktop widgets,
  // browser history) — leaf-level records with no generic-tree presentation.
  'Task',
  'Area',
  'Job',
  'Schedule',
  'Terminal',
  'Browser.Visit',
  'AppInstance',
  'System.AppInstance',

  // Browser app internals — BrowserWindow/BrowserPage are legacy leftovers
  // from before browser windows/tabs became fully ephemeral (in-memory only,
  // never persisted — see gui/src/apps/BrowserApp/store/browserAppStore.ts).
  // BrowserDomainSession is an even older, fully superseded snapshot format
  // (see backend/services/browserbase/contexts.py). BrowserProfile is a
  // real, still-created system entity (one per dataspace, anchors the
  // Browserbase Context that persists cookies/login state across ephemeral
  // sessions — see backend/routes/browser_profile.py) but is internal
  // bookkeeping, never a user-organizable document. BrowserSession
  // (saved/restorable tab sets, a real user-facing feature) is deliberately
  // NOT in this list.
  'BrowserWindow',
  'BrowserPage',
  'BrowserDomainSession',
  'BrowserProfile',

  // Legacy intelligence/memory types — apps deleted, rows orphaned in prod.
  // Hidden so they don't appear as unknown-type entries in generic lists.
  'Memory',
  'System.Memory',
  'System.UserProfile',
]);

/**
 * Prefix rules for types that carry a dotted subtype suffix, e.g.
 * "Integration.Gmail" or "External.GoogleDriveFile".
 */
export const GENERIC_TREE_HIDDEN_PREFIXES: readonly string[] = [
  'Integration.',
  'External.GoogleDrive',
  'External.LocalFile',
];

/**
 * Pure entity-type check: does this type belong in the shared "never a
 * generic tree row" set? Callers layer their own additional, surface-specific
 * exclusions and non-type-based checks (trashed, sourceImportMethod,
 * isDuplicate, embedded, hidden flags, …) on top of this.
 */
export function isGenericTreeHiddenType(entityType: string | undefined | null): boolean {
  if (!entityType) return false;
  if (GENERIC_TREE_HIDDEN_ENTITY_TYPES.has(entityType)) return true;
  return GENERIC_TREE_HIDDEN_PREFIXES.some((prefix) => entityType.startsWith(prefix));
}
